/*
 * The product page's buy column (pdp-redesign, owner, 7 Oct 2026). Without
 * this file the page still works: the form posts the variant rendered as
 * selected (with sets, the checked radio, which is the id field itself), every
 * format is a real radio, and the notebooks and grandparent rows, which need
 * script, are hidden.
 *
 * ONE STATE: the chosen entry (a variant, or a set; dg-pdp-entries.liquid),
 * whether the notebooks are added, and the grandparent quantity. From it:
 *   - what is sold: the entry, or its upgrade product while the notebooks are
 *     added. Its id goes in the form's id field, so the one /cart/add.js call
 *     dg-cart.js makes adds that product, and the add-on beside it from a row
 *     that is offered.
 *   - Row A: the selected format's name and its note (one cell, only the
 *     selected note visible, so the row never changes height).
 *   - Row B: offered while the entry has an upgrade; its per-notebook price,
 *     the card's added state, the button's aria-pressed, and its one tag,
 *     tested against the cart total plus everything configured here.
 *   - Row C: offered while what is sold is addon_eligible (the cart's price
 *     band is not used on this page); the card is selected while the quantity
 *     is above 0.
 *   - the order summary (piece 3): the free-shipping band, what is bought,
 *     its parts, the grandparent guides, the total and the undiscounted total
 *     struck beside it, and the shipping tick while the threshold is reached.
 *   - the URL's ?variant= (a set writes none: its id is another product's).
 * THE SHIPPING STATUS COUNTS NOTHING TWICE (tweak round 1, owner, 7 Oct
 * 2026). The band, the notebooks tag and the shipping tick test (cart total +
 * what is PENDING here). Pending is the current selection at load; after a
 * successful add (dg:added, from dg-cart.js) it is zero, because the cart now
 * holds the selection; it is the current selection again as soon as the
 * shopper changes the format, the notebooks or the grandparent quantity. The
 * summary's name and total always show what the button would add.
 * Every figure is computed from the entries' cents and written in the shop's
 * money format; no price is read from the page. The new cart total arrives
 * from dg-cart.js as dg:cart. After every change the root carries the summary
 * (dgSummary: name, "+ n grandparent guides" or '', total, and the chosen
 * entry's id and whether the notebooks are added) and dispatches dg:pdp for
 * the purchase bar and the gallery.
 * THE BAND CELEBRATES (tweak round 8, item 2, owner ruling, 8 Oct 2026).
 * When the shopper's change of the format, the notebooks or the grandparent
 * quantity moves the band from away to reached, the band dispatches
 * dg:celebrate after it and the total are drawn, and dg-cart.js plays the
 * cart's sparkles on it. Every time a choice does so (tweak round 9, item 1,
 * owner ruling, 8 Oct 2026, lifting round 8's once per page view); never on
 * load, after an add, or when the cart's new total (dg:cart) moves the band.
 * THE PILL SPARKLES TOO (pdp-pill-sparkle, owner brief, 9 Oct 2026). When the
 * same kind of change turns the notebooks pill to "Free Shipping Unlocked",
 * the pill dispatches dg:celebrate with { around, scale }: the same sparkles,
 * in a ring outside it, sized by the pill's height over the band's. Never on
 * load or when it leaves that state. When one change would celebrate both,
 * only the pill plays; the band still turns and reads reached (owner, 10 Oct
 * 2026). No event of its own: pdp_free_shipping_reached stays the band's and
 * still fires on every crossing.
 * BEHAVIOUR EVENTS (shop events, owner brief, 9 Oct 2026), through
 * DGEvents.track (assets/dg-events.js, which decides whether anything is
 * sent): pdp_format_selected (format: paperback | workbook, from the format
 * row only), pdp_notebooks_toggled (added), pdp_grandparent_qty_changed
 * (quantity, when the stepper's value actually changes),
 * pdp_free_shipping_reached (exactly when the band celebrates), and
 * pdp_add_to_cart (source: page | bar, has_notebooks, grandparent_quantity,
 * value), sent once dg-cart.js reports the add went through (dg:added), from
 * what was configured when the form was submitted.
 */
(() => {
  for (const root of document.querySelectorAll('[data-dg-product]')) {
    const data = root.querySelector('[data-dg-variants]');
    if (!data) continue;
    const entries = JSON.parse(data.textContent);
    const money = root.dataset.dgMoneyFormat ?? '';
    const threshold = Number(root.dataset.dgThreshold) || 0;
    let cartCents = Number(root.dataset.dgCartCents) || 0;
    let added = false;
    // Whether the selection here counts toward the shipping status (above).
    let pending = true;

    const idInput = root.querySelector('[data-dg-variant-id]');
    const states = root.querySelectorAll('[data-dg-state]');
    const formatName = root.querySelector('[data-dg-format-name]');
    const summary = root.querySelector('[data-dg-summary]');
    const band = summary?.querySelector('[data-dg-band]');
    const bandText = summary?.querySelector('[data-dg-band-text]');
    const field = (name) => summary?.querySelector(`[data-dg-summary-${name}]`);

    const nbRow = root.querySelector('[data-dg-notebooks]');
    const nbTag = nbRow?.querySelector('[data-dg-nb-tag]');
    const nbCard = nbRow?.querySelector('[data-dg-nb-card]');
    const nbEach = nbRow?.querySelector('[data-dg-nb-each]');
    const nbToggle = nbRow?.querySelector('[data-dg-nb-toggle]');
    const words = nbRow?.dataset ?? {};

    const addon = root.querySelector('[data-dg-addon]');
    const addonQty = addon?.querySelector('[data-dg-addon-qty]');
    const addonCard = addon?.querySelector('[data-dg-addon-card]');
    const addonCents = Number(addon?.dataset.dgAddonCents) || 0;
    const addonFull = Number(addon?.dataset.dgAddonCompareCents) || addonCents;
    const form = root.querySelector('[data-dg-product-form]');
    const track = (name, props, origin) => window.DGEvents?.track(name, props, { origin });
    // The stepper's last value, so only a real change is reported.
    let reportedQty = null;
    // What the submitted form held, reported once the add goes through.
    let submitted = null;

    // The shop's own money format, as money_without_trailing_zeros writes it:
    // two decimals, dropped when they are .00.
    function format(cents) {
      const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',').replace(/\.00$/, '');
      return money.replace(/\{\{\s*\w+\s*\}\}/, amount);
    }

    function chosenEntry() {
      const radio = root.querySelector('[data-dg-choice]:checked');
      if (radio) return entries.find((v) => String(v.id) === radio.value);
      const chosen = [...root.querySelectorAll('[data-dg-option]:checked')].map((input) => input.value);
      // No choice on the page (a one-variant product): its one entry.
      if (!chosen.length) return entries.find((v) => !v.set);
      return entries.find((v) => !v.set && v.options.every((value, i) => value === chosen[i]));
    }

    const quantity = () => Math.max(0, Number.parseInt(addonQty?.value, 10) || 0);

    // Everything configured here: what is sold, how many add-ons go with it,
    // the total, the undiscounted total, and the shipping status's figure: the
    // cart's total plus what is pending here.
    function configured(entry) {
      const sold = (added && entry.upgrade) || entry;
      const offered = Boolean(addon) && Boolean(sold.eligible);
      const guides = offered ? quantity() : 0;
      const total = sold.cents + guides * addonCents;
      const full = (sold.compare_cents ?? sold.cents) + guides * addonFull;
      return { sold, offered, guides, total, full, status: cartCents + (pending ? total : 0) };
    }

    // "Would unlock" asks what pressing Add would make the status: the cart's
    // total plus this selection with the notebooks, which is then pending.
    function tag(entry, now) {
      const up = entry.upgrade;
      if (!added && threshold && now.status < threshold && cartCents + now.total - now.sold.cents + up.cents >= threshold) {
        return { text: words.dgWould, tone: 'grey' };
      }
      if (added && threshold && now.status >= threshold) return { text: words.dgUnlocked, tone: 'green' };
      if (up.compare_cents > up.cents) {
        const saved = Math.round((up.compare_cents - up.cents) / 100) * 100;
        return { text: words.dgSave.replace('[amount]', format(saved)), tone: 'plain' };
      }
      return { text: '', tone: '' };
    }

    // THE ORDER SUMMARY (piece 3). A line that comes and goes carries
    // data-dg-empty, which takes it out of the layout: the box fits its
    // contents (tweak round 1, owner, 7 Oct 2026).
    function show(element, text) {
      if (!element) return;
      element.toggleAttribute('data-dg-empty', !text);
      element.textContent = text || '\u00a0';
    }

    // A variant's name with its lead-in in italics and lower case, as
    // snippets/dg-variant-name.liquid draws it (tweak round 9, item 6a):
    // "enough for a Paperback". Without one, the name as text.
    function named(element, item) {
      if (!element) return;
      element.toggleAttribute('data-dg-empty', !item.name);
      const lead = item.lead && item.name.startsWith(item.lead) ? item.lead : null;
      if (!lead) {
        element.textContent = item.name || '\u00a0';
        return;
      }
      const span = document.createElement('span');
      span.className = 'dg-lead-in';
      span.textContent = lead.toLowerCase();
      element.replaceChildren(span, item.name.slice(lead.length));
    }

    function draw(now) {
      const reached = Boolean(threshold) && now.status >= threshold;
      if (band && bandText) {
        band.dataset.state = reached ? 'reached' : 'away';
        // A quantity group names the fewest copies that ship free instead of
        // the amount away (owner ruling, 16 Oct 2026; dg-pdp-summary.liquid).
        bandText.textContent = reached ? summary.dataset.dgReached : summary.dataset.dgFreeOn || summary.dataset.dgAway.replace('[amount]', format(threshold - now.status));
      }
      named(field('name'), now.sold);
      show(field('parts'), now.sold.parts);
      show(field('guides'), now.guides === 0 ? '' : now.guides === 1 ? summary.dataset.dgOne : summary.dataset.dgOther.replace('[n]', String(now.guides)));
      const total = field('total');
      if (total) total.textContent = format(now.total);
      const full = field('full');
      if (full) {
        full.textContent = now.full > now.total ? format(now.full) : '';
        full.closest('s')?.toggleAttribute('data-dg-empty', !(now.full > now.total));
      }
      field('shipping')?.toggleAttribute('data-dg-empty', !reached);
    }

    // The notebooks pill reads "Free Shipping Unlocked" (pdp-pill-sparkle).
    const unlocked = () => Boolean(nbTag) && !nbRow.hasAttribute('data-dg-off') && !nbTag.hidden && nbTag.dataset.tone === 'green';

    // byShopper: the shopper changed a choice, so the band may celebrate.
    function apply(entry, byShopper = false) {
      if (!entry) return;
      const before = band?.dataset.state;
      const wasUnlocked = unlocked();
      if (!entry.upgrade) added = false;
      const now = configured(entry);

      if (idInput) idInput.value = now.sold.id;
      named(formatName, entry);
      for (const state of states) state.toggleAttribute('data-dg-off', state.dataset.dgState !== String(entry.id));

      if (nbRow) {
        nbRow.toggleAttribute('data-dg-off', !entry.upgrade);
        nbCard?.toggleAttribute('data-dg-added', added);
        nbToggle?.setAttribute('aria-pressed', String(added));
        if (entry.upgrade) {
          const difference = entry.upgrade.cents - entry.cents;
          const each = format(Math.floor(difference / 2));
          if (nbEach) nbEach.textContent = (difference % 2 === 0 ? words.dgEach : words.dgEachAbout).replace('[price]', each);
          const { text, tone } = tag(entry, now);
          if (nbTag) {
            // Rewritten only when it changes, so a redraw does not cut a sparkle short.
            if (nbTag.textContent !== text) nbTag.textContent = text;
            nbTag.dataset.tone = tone;
            nbTag.hidden = !text;
          }
        }
      }

      if (addon) {
        addon.toggleAttribute('data-dg-off', !now.offered);
        addonCard?.toggleAttribute('data-dg-added', now.offered && now.guides > 0);
      }

      if (summary) draw(now);
      // One change that would celebrate both plays only the pill's (owner, 10 Oct 2026).
      const pillPlays = byShopper && !wasUnlocked && unlocked();
      if (byShopper && before === 'away' && band?.dataset.state === 'reached') {
        if (!pillPlays) band.dispatchEvent(new CustomEvent('dg:celebrate', { bubbles: true }));
        track('pdp_free_shipping_reached', {}, band);
      }
      if (pillPlays) {
        const scale = band?.offsetHeight ? nbTag.offsetHeight / band.offsetHeight : 1;
        nbTag.dispatchEvent(new CustomEvent('dg:celebrate', { bubbles: true, detail: { around: true, scale } }));
      }

      const guides = now.guides === 0 || !summary ? '' : now.guides === 1 ? summary.dataset.dgOne : summary.dataset.dgOther.replace('[n]', String(now.guides));
      // entry and added also tell the gallery what to show (tweak round 3, item 3).
      // cents and count are the total and the grandparent guides as numbers,
      // for pdp_add_to_cart.
      root.dgSummary = { name: now.sold.name, guides, total: format(now.total), entry: String(entry.id), added, cents: now.total, count: now.guides };
      root.dispatchEvent(new CustomEvent('dg:pdp', { bubbles: true, detail: root.dgSummary }));
    }

    function remember(entry) {
      if (!entry) return;
      const url = new URL(window.location.href);
      if (entry.set) url.searchParams.delete('variant');
      else url.searchParams.set('variant', entry.id);
      window.history.replaceState({}, '', url);
    }

    // The format row's choice as one word, or null for any other choice.
    function formatWord(input) {
      if (!input.closest('.dg-format')) return null;
      const value = input.value.toLowerCase();
      if (value.includes('workbook')) return 'workbook';
      if (value.includes('paperback')) return 'paperback';
      return null;
    }

    function reportQty(origin) {
      if (!addon || quantity() === reportedQty) return;
      reportedQty = quantity();
      track('pdp_grandparent_qty_changed', { quantity: reportedQty }, origin);
    }

    // Every change of a choice here makes the selection pending again.
    root.addEventListener('change', (event) => {
      if (event.target.matches('[data-dg-option], [data-dg-choice]')) {
        pending = true;
        const entry = chosenEntry();
        apply(entry, true);
        remember(entry);
        const word = formatWord(event.target);
        if (word) track('pdp_format_selected', { format: word }, event.target);
      } else if (event.target === addonQty) {
        pending = true;
        addonQty.value = String(quantity());
        apply(chosenEntry(), true);
        reportQty(addonQty);
      }
    });

    addonQty?.addEventListener('input', () => {
      pending = true;
      apply(chosenEntry(), true);
    });

    root.addEventListener('click', (event) => {
      if (!(event.target instanceof Element)) return;
      const toggle = event.target.closest('[data-dg-nb-toggle]');
      if (toggle) {
        pending = true;
        added = !added;
        apply(chosenEntry(), true);
        track('pdp_notebooks_toggled', { added }, toggle);
        return;
      }
      const step = event.target.closest('[data-dg-addon-step]');
      if (step && addonQty) {
        pending = true;
        addonQty.value = String(Math.max(0, quantity() + Number.parseInt(step.dataset.dgAddonStep, 10)));
        apply(chosenEntry(), true);
        reportQty(step);
      }
    });

    // Read before dg-cart.js's own submit handler, on the document, sets the
    // stepper back to 0. The purchase bar's buttons submit this same form.
    form?.addEventListener('submit', (event) => {
      if (event.submitter?.name === 'checkout') return;
      const summary = root.dgSummary ?? {};
      submitted = {
        source: event.submitter?.matches('[data-dg-buybar-action]') ? 'bar' : 'page',
        has_notebooks: Boolean(summary.added),
        grandparent_quantity: summary.count || 0,
        value: (summary.cents || 0) / 100,
      };
    });

    // dg-cart.js reports the new cart after every change, and has set the
    // stepper back to 0 after an add.
    document.addEventListener('dg:cart', (event) => {
      if (Number.isFinite(event.detail?.totalCents)) cartCents = event.detail.totalCents;
      apply(chosenEntry());
    });

    // The add went through: the cart now holds the selection, so nothing here
    // is pending until the shopper changes a choice (tweak round 1). The
    // stepper is back at 0 (dg-cart.js), as it is after any add.
    root.addEventListener('dg:added', (event) => {
      if (submitted && event.target === form) track('pdp_add_to_cart', submitted, form);
      submitted = null;
      pending = false;
      if (addonQty) addonQty.value = '0';
      reportedQty = quantity();
      apply(chosenEntry());
    });

    apply(chosenEntry());
    reportedQty = quantity();
  }
})();

/*
 * THE PURCHASE BAR (owner, 6 Oct 2026). Rendered hidden by dg-product.liquid;
 * this reveals it, so with no script there is none. It mirrors the buy box:
 * the name and total of what is configured there (root.dgSummary, updated on
 * dg:pdp; pdp-redesign piece 2), and one action that submits the product form,
 * so it adds exactly what the page's own button would.
 *
 * WHEN IT SHOWS (pdp-redesign piece 6, owner, 7 Oct 2026). Below 990 (tweak
 * round 9, item 5, owner ruling, 8 Oct 2026): as soon as ANY part of the
 * form's own button is off screen, and hidden only while the whole button is
 * on screen, from the top of the screen to the top of the bar's own box (the
 * area the bar would cover, as before). It used to hide while any part of the
 * button showed. From 990: only once that button has scrolled above the top
 * of the screen; never while it is still below, and never at the top of the
 * page.
 * It is also hidden while the cart drawer or the mobile menu is open, and while
 * CookieYes's notice is taller than half the viewport (tweak round 10). Its first
 * state is set before transitions are switched on, so on load it is simply
 * present or absent; after that it arrives and leaves on motion.chromeSlide
 * (dg-shop.css). The page gains bottom padding of the bar's measured height, so
 * the footer's last line can scroll clear of it. While CookieYes's notice
 * shows, the bar sits directly above it (bar-above-notice, owner, 8 Oct 2026;
 * dg-notice.js measures it), so the padding grows by the notice's height and
 * the area the bar covers starts that much higher. While the bar shows below 990
 * the menu's round open control sits over the bar's right-hand gap, in its
 * light style (dg-shop.css; tweak round 2, item 2).
 *
 * TWO LAYOUTS (tweak round 2, item 1, owner, 7 Oct 2026). From 990 the name,
 * the total and a worded button, as before. Below 990 the summary's name, a
 * second line "+ n grandparent guide(s)" while the stepper is above 0, and a
 * button holding the glyph and the total, whose accessible name this writes:
 * "Add to cart, {name}, {guides}, {total}". Both buttons submit the form.
 *
 * THE TEXT FITS ITS SPACE (tweak round 3, item 4, owner, 7 Oct 2026). Below
 * 990 the bar is 84px tall (dg-shop.css) and fit() sets the text to the
 * largest size, from the small step up to the body step in half pixels, at
 * which it sits on at most two lines in the width it has: the name on up to
 * two, or the name on one and "+ n grandparent guide(s)" on the second. At
 * the small step each line steps on down alone, to the micro size (12px) at
 * least: the second line, which never ends in an ellipsis (tweak round 4,
 * item 6, owner, 7 Oct 2026), and since tweak round 5, item 7 (owner, 7 Oct
 * 2026) the first as well, whose floor was the small step; what still does
 * not fit the first line at the micro size ends in an ellipsis. The whole text
 * stays in the page, and in the button's name, for a screen reader. It runs
 * whenever the text changes, on resize and once the fonts have loaded, and
 * before the bar first shows, so it never shows unfitted. The steps are read
 * from the tokens in rem, so a reader's own text size moves them.
 */
(() => {
  const bar = document.querySelector('[data-dg-buybar]');
  const actions = [...(bar?.querySelectorAll('[data-dg-buybar-action]') ?? [])];
  const form = actions[0]?.form;
  const own = form?.querySelector('.dg-product__add');
  const root = form?.closest('[data-dg-product]');
  if (!bar || !own || !root) return;

  const name = bar.querySelector('[data-dg-buybar-name]');
  const price = bar.querySelector('[data-dg-buybar-price]');
  const line = bar.querySelector('[data-dg-buybar-line]');
  const guides = bar.querySelector('[data-dg-buybar-guides]');
  const narrow = bar.querySelector('[data-dg-buybar-narrow]');
  const total = bar.querySelector('[data-dg-buybar-total]');
  const fallbackName = name.textContent;
  const dialog = document.querySelector('[data-dg-cart]');
  const shell = document.querySelector('.dg-shell');
  const what = bar.querySelector('.dg-buybar__what');

  // A type token in px: rem against the root's own size, which follows the
  // reader's settings.
  function px(token) {
    const html = document.documentElement;
    const value = getComputedStyle(html).getPropertyValue(token).trim();
    const number = Number.parseFloat(value);
    return value.endsWith('rem') ? number * Number.parseFloat(getComputedStyle(html).fontSize) : number;
  }

  function fit() {
    if (!what || !line || getComputedStyle(what).display === 'none') return;
    const two = Boolean(guides) && !guides.hidden;
    what.toggleAttribute('data-dg-guides', two);
    const lines = two ? [line, guides] : [line];
    const floor = px('--dg-small-size');
    const ceiling = px('--dg-body-size');
    const fits = () => lines.every((el) => el.scrollWidth <= el.clientWidth + 0.5 && el.scrollHeight <= el.clientHeight + 0.5)
      && what.scrollHeight <= what.clientHeight + 0.5;
    line.style.fontSize = '';
    if (guides) {
      guides.style.fontSize = '';
      guides.style.whiteSpace = '';
    }
    for (let size = ceiling; size > floor; size -= 0.5) {
      what.style.fontSize = `${size}px`;
      if (fits()) return;
    }
    what.style.fontSize = `${floor}px`;
    if (fits()) return;
    // Below the small step each line steps down alone, to the micro size at
    // least: the second so it always shows in full (tweak round 4, item 6),
    // the first so "Paperback + Notebooks" shows in full (tweak round 5,
    // item 7, owner, 7 Oct 2026: its floor drops to the second line's). The
    // name alone, on up to two lines, steps down the same way.
    const least = px('--dg-micro-size');
    if (!two) {
      for (let size = floor - 0.5; size >= least; size -= 0.5) {
        what.style.fontSize = `${size}px`;
        if (fits()) return;
      }
      return;
    }
    const whole = (el) => el.scrollWidth <= el.clientWidth + 0.5;
    for (const el of lines) {
      for (let size = floor - 0.5; size >= least && !whole(el); size -= 0.5) el.style.fontSize = `${size}px`;
    }
    if (!whole(guides)) guides.style.whiteSpace = 'normal';
  }

  // The summary's name and total (dg:pdp, written by the buy column above).
  // On a gift card the product's name, never the amount's (tweak round 3, item 5).
  function update() {
    const summary = root.dgSummary;
    if (!summary) return;
    const label = bar.dataset.dgBarName || summary.name || fallbackName;
    name.textContent = label;
    price.textContent = summary.total;
    if (line) line.textContent = label;
    if (guides) {
      guides.textContent = summary.guides || '';
      guides.hidden = !summary.guides;
    }
    if (total) total.textContent = summary.total;
    if (narrow) {
      const parts = [narrow.dataset.dgAddWord, label, summary.guides, summary.total].filter(Boolean);
      narrow.setAttribute('aria-label', parts.join(', '));
    }
    fit();
  }

  const wide = window.matchMedia('(min-width: 990px)');

  // The cookie notice's height, 0 with none (dg-notice.js).
  const notice = () => Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--dg-cookie-notice-height')) || 0;

  function place() {
    // While the notice is taller than half the viewport (CookieYes's EU notice
    // after "Customise"), the bar is hidden: not drawn, not focusable, not
    // announced (tweak round 10, part B1, owner, 9 Oct 2026). It returns under
    // the rules below once the notice shrinks or goes; dg:notice re-runs this.
    const tall = notice() > window.innerHeight / 2;
    const covered = tall || Boolean(dialog?.open) || Boolean(shell?.hasAttribute('data-dg-menu-open'));
    const box = own.getBoundingClientRect();
    const passed = box.bottom <= 0;
    const whole = box.top >= 0 && box.bottom <= window.innerHeight - bar.offsetHeight - notice();
    const wanted = wide.matches ? passed : !whole;
    bar.toggleAttribute('data-dg-shown', !covered && wanted);
  }

  let frame = 0;
  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      place();
    });
  }

  bar.hidden = false;
  update();
  place();
  // The bar's rendered height, and the notice's, is the page's bottom padding.
  // Measured, never typed.
  new ResizeObserver(() => {
    document.body.style.paddingBlockEnd = `calc(${bar.offsetHeight}px + var(--dg-cookie-notice-height, 0px))`;
    schedule();
  }).observe(bar);

  // Transitions only after the first state has painted.
  requestAnimationFrame(() => requestAnimationFrame(() => bar.setAttribute('data-dg-ready', '')));

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', () => {
    fit();
    schedule();
  });
  document.fonts?.ready.then(fit);
  wide.addEventListener('change', schedule);
  document.addEventListener('dg:notice', schedule);
  const watch = new MutationObserver(schedule);
  if (dialog) watch.observe(dialog, { attributes: true, attributeFilter: ['open'] });
  if (shell) watch.observe(shell, { attributes: true, attributeFilter: ['data-dg-menu-open'] });

  root.addEventListener('dg:pdp', update);
  const release = () => {
    for (const action of actions) action.removeAttribute('aria-disabled');
  };
  document.addEventListener('dg:cart', release);
  // A refused gift card add stays on the page (tweak round 9, item 6c).
  form.addEventListener('dg:add-error', release);

  // One press, one add: held until dg-cart.js reports the new cart. Should the
  // add fail, dg-cart.js posts the form itself and the page leaves.
  form.addEventListener('submit', (event) => {
    if (actions.includes(event.submitter)) {
      for (const action of actions) action.setAttribute('aria-disabled', 'true');
    }
  });
})();

/*
 * THE GALLERY (B1, owner, 6 Oct 2026). Only on a product with more than one
 * image (dg-product.liquid marks it data-dg-gallery); with no script the
 * column stays stacked. This sets it ready, which turns the column into one
 * main image that scrolls sideways (native scroll-snap, so it swipes) and
 * reveals the thumbnail strip. A thumbnail moves the main image at once, with
 * no scripted animation; the current thumbnail follows the main image however
 * it was moved. The gallery follows the choice: a variant shows its featured
 * media. It starts on the chosen choice's slide.
 *
 * FOLLOWS THE SET (owner ruling, 21 Oct 2026). Without formats, where the
 * product carries sets (dg-product-gallery.liquid marks their slides
 * data-dg-set-owner, the product's own data-dg-own), only the chosen choice's
 * slides show: the product's own while its own choice is chosen, a set's
 * media while that set is. The rest and their thumbnails are hidden, the
 * thumbnails' names renumbered over what shows, as for formats. Switching
 * to another set, or between a set and the product's own, returns to the
 * first slide shown; switching between the product's own variants still
 * moves to that variant's media.
 *
 * FILTERED BY FORMAT (tweak round 3, item 3, owner, 7 Oct 2026). Where the
 * gallery carries data-dg-formats (dg-product-gallery.liquid), only the
 * selected format's slides show: its main image, its gallery files, the
 * shared images, and the set's image while the notebooks are added. Another
 * format's slides and thumbnails are hidden, the thumbnails' names are
 * renumbered over what shows, and with one slide left there is no strip. It
 * reads the selection from the buy column (root.dgSummary, dg:pdp). Choosing a
 * format returns to its main image; adding the notebooks moves to the set's
 * image (ASSUMED in the run log), removing them back to the main image.
 * The quantity group's and the gift cards' galleries carry data-dg-formats
 * too, and follow the selected variant here the same way (owner ruling,
 * widened, 21 Oct 2026; dg-product.liquid).
 *
 * VIDEO (owner rulings, 21 Oct 2026; dg-gallery-video.liquid). A loop video
 * (data-dg-video="loop") is muted, loops and has no controls. This plays it
 * only while it is the current slide, not hidden by the format filter, the
 * main image is at least half on screen and the tab is showing; otherwise it
 * is paused, so a thumbnail, a swipe, a format change, scrolling away or
 * leaving the tab pauses it, and coming back resumes it. Under
 * prefers-reduced-motion it never plays, and where play is refused (iOS Low
 * Power Mode) the poster stays. A standard player (data-dg-video="player")
 * is never started here; it is only paused when its slide is left (or hidden
 * by the filter) or scrolled away, and is not resumed on return.
 */
(() => {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  for (const gallery of document.querySelectorAll('[data-dg-gallery]')) {
    const track = gallery.querySelector('[data-dg-gallery-track]');
    const strip = gallery.querySelector('[data-dg-gallery-thumbs]');
    const root = gallery.closest('[data-dg-product]');
    if (!track || !strip || !root) continue;
    const slides = [...track.querySelectorAll('[data-dg-slide]')];
    const thumbs = [...strip.querySelectorAll('[data-dg-thumb]')];
    const data = root.querySelector('[data-dg-variants]');
    const variants = data ? JSON.parse(data.textContent) : [];
    const formats = gallery.hasAttribute('data-dg-formats');
    const owned = slides.some((slide) => slide.dataset.dgSetOwner);

    gallery.dataset.dgGallery = 'ready';
    strip.hidden = false;

    const shown = () => slides.filter((slide) => !slide.hidden);

    // The video slides, their current slide, and whether the main image is on
    // screen (watched only where there is a video).
    const videos = slides.map((slide) => slide.querySelector('video'));
    let current = slides[0];
    let onScreen = false;
    function play() {
      slides.forEach((slide, i) => {
        const video = videos[i];
        if (!video) return;
        const showing = slide === current && !slide.hidden && onScreen;
        if (slide.dataset.dgVideo !== 'loop') {
          if (!showing && !video.paused) video.pause();
        } else if (showing && !document.hidden && !still.matches) {
          if (video.paused) video.play()?.catch(() => {});
        } else if (!video.paused) video.pause();
      });
    }
    if (videos.some(Boolean)) {
      new IntersectionObserver(([entry]) => {
        onScreen = entry.intersectionRatio >= 0.5;
        play();
      }, { threshold: 0.5 }).observe(track);
      document.addEventListener('visibilitychange', play);
      still.addEventListener('change', play);
    }

    // A changed slide brings its thumbnail into view; force does it anyway.
    function mark(slide, force = false) {
      const moved = Boolean(slide) && (force || slide !== current);
      if (slide) current = slide;
      play();
      thumbs.forEach((thumb, i) => {
        if (slides[i] === slide) {
          thumb.setAttribute('aria-current', 'true');
          if (moved) reveal(thumb);
        } else thumb.removeAttribute('aria-current');
      });
    }

    /*
     * OVERFLOW (owner rulings, 21 Oct 2026; dg-shop.css, dg-pdp.css). From
     * 990 the thumbnails are a vertical rail, below it a sideways strip, and
     * either holds more than it shows. The current thumbnail comes fully into
     * view with its neighbour beyond it: the neighbour is the thumbnail's own
     * scroll margin, read from CSS, and only the rail or strip scrolls, never
     * the page (no scrollIntoView). A fade shows at each end with thumbnails
     * out of view, and on the rail an arrow over it, which scrolls the rail by
     * the thumbnails it shows less one. Smooth, instant under reduced motion.
     */
    const wide = window.matchMedia('(min-width: 990px)');
    const behavior = () => (still.matches ? 'instant' : 'smooth');
    const ends = [...gallery.querySelectorAll('[data-dg-gallery-fade], [data-dg-gallery-more]')];
    const arrows = ends.filter((end) => end.matches('[data-dg-gallery-more]'));
    const axis = () => (wide.matches
      ? { from: 'top', to: 'bottom', at: 'scrollTop', size: 'clientHeight', whole: 'scrollHeight', start: 'scrollMarginTop', end: 'scrollMarginBottom' }
      : { from: 'left', to: 'right', at: 'scrollLeft', size: 'clientWidth', whole: 'scrollWidth', start: 'scrollMarginLeft', end: 'scrollMarginRight' });

    function reveal(thumb) {
      const a = axis();
      if (strip[a.whole] <= strip[a.size]) return;
      const item = thumb.parentElement;
      const margin = getComputedStyle(item);
      const box = item.getBoundingClientRect();
      const offset = strip.getBoundingClientRect()[a.from] - strip[a.at];
      const from = box[a.from] - offset - parseFloat(margin[a.start]);
      const to = box[a.to] - offset + parseFloat(margin[a.end]);
      // A pixel of slack, so a subpixel layout does not count as out of view.
      let target = null;
      if (from < strip[a.at] - 1) target = from;
      else if (to > strip[a.at] + strip[a.size] + 1) target = to - strip[a.size];
      if (target !== null) strip.scrollTo({ [a.from]: target, behavior: behavior() });
    }

    function edges() {
      const a = axis();
      const more = {
        start: strip[a.at] > 1,
        end: strip[a.at] + strip[a.size] < strip[a.whole] - 1,
      };
      const focused = arrows.find((arrow) => arrow === document.activeElement);
      for (const end of ends) end.hidden = !more[end.dataset.dgGalleryFade ?? end.dataset.dgGalleryMore];
      // An arrow that hides while focused hands focus to the other, or to the
      // current thumbnail, so it is not dropped to the page.
      if (focused?.hidden) {
        const next = arrows.find((arrow) => !arrow.hidden) ?? thumbs.find((thumb) => thumb.hasAttribute('aria-current'));
        next?.focus({ preventScroll: true });
      }
    }

    // The rail's gap, and one thumbnail with its gap.
    function step() {
      const item = [...strip.children].find((li) => !li.hidden);
      const gap = parseFloat(getComputedStyle(strip).rowGap) || 0;
      return { gap, one: item ? item.getBoundingClientRect().height + gap : 0 };
    }

    for (const arrow of arrows) {
      arrow.addEventListener('click', () => {
        const { gap, one } = step();
        if (!one) return;
        // How many show whole; a hundredth of slack for subpixel sizes.
        const showing = Math.floor((strip.clientHeight + gap) / one + 0.01);
        const sign = arrow.dataset.dgGalleryMore === 'end' ? 1 : -1;
        strip.scrollBy({ top: sign * Math.max(showing - 1, 1) * one, behavior: behavior() });
      });
      // A wheel or trackpad over an arrow scrolls the rail under it, not the
      // page. Snapping waits until the wheel stops, so a trackpad's small
      // steps move it; then the rail settles on a thumbnail.
      arrow.addEventListener('wheel', (event) => {
        event.preventDefault();
        const unit = [1, step().one, strip.clientHeight][event.deltaMode] ?? 1;
        strip.style.scrollSnapType = 'none';
        strip.scrollBy({ top: event.deltaY * unit, behavior: 'instant' });
        clearTimeout(arrow.dgRest);
        arrow.dgRest = setTimeout(() => strip.style.removeProperty('scroll-snap-type'), 150);
      }, { passive: false });
    }

    let edgeFrame = 0;
    strip.addEventListener('scroll', () => {
      if (edgeFrame) return;
      edgeFrame = requestAnimationFrame(() => {
        edgeFrame = 0;
        edges();
      });
    }, { passive: true });
    new ResizeObserver(edges).observe(strip);

    function show(slide) {
      if (!slide || slide.hidden) return;
      track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'instant' });
      mark(slide, true);
    }

    // The slide for a choice: the variant's media; none for a set.
    function slideFor(id) {
      const entry = variants.find((v) => String(v.id) === String(id));
      if (!entry) return null;
      // A product's own choice with no media of its own (a one-variant product
      // beside its sets, pdp-s3 piece 2) returns to the product's first image.
      if (!entry.media) return entry.set ? null : slides[0];
      return slides.find((slide) => slide.dataset.dgMedia === String(entry.media)) ?? null;
    }

    function chosenId() {
      const radio = root.querySelector('[data-dg-choice]:checked');
      if (radio) return radio.value;
      return root.querySelector('[data-dg-variant-id]')?.value;
    }

    // Only the slides that pass, their thumbnails renumbered over them.
    function only(passes) {
      slides.forEach((slide, i) => {
        const on = passes(slide);
        slide.hidden = !on;
        thumbs[i].parentElement.hidden = !on;
      });
      const visible = shown();
      const label = strip.dataset.dgThumbLabel ?? '';
      visible.forEach((slide, k) => {
        thumbs[slides.indexOf(slide)].setAttribute('aria-label', label.replace('[index]', String(k + 1)).replace('[count]', String(visible.length)));
      });
      strip.hidden = visible.length <= 1;
      play();
    }

    // Only the selected format's slides (formats only).
    let selected = null;
    function filter(selection) {
      const { entry, added } = selection;
      only((slide) => {
        const { dgOwner: owner, dgUpgradeOf: upgradeOf } = slide.dataset;
        return owner ? owner === entry : upgradeOf ? upgradeOf === entry && added : true;
      });
    }

    // Only the chosen set's slides, or the product's own (sets only). The
    // group is the set's variant id, or 'own'; true when it changed.
    let group = null;
    function pick(id) {
      const set = slides.some((slide) => slide.dataset.dgSetOwner === String(id));
      const next = set ? String(id) : 'own';
      if (next === group) return false;
      group = next;
      only((slide) => (set ? slide.dataset.dgSetOwner === next : slide.hasAttribute('data-dg-own')));
      return true;
    }

    function choose(first = false) {
      const id = chosenId();
      const changed = pick(id);
      if (changed && !first) {
        strip.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        show(shown()[0]);
      } else {
        const own = slideFor(id);
        if (own && !own.hidden) show(own);
        else if (first) show(shown()[0]);
      }
      edges();
    }

    function follow() {
      const summary = root.dgSummary;
      if (!summary?.entry) return;
      const next = { entry: summary.entry, added: Boolean(summary.added) };
      if (selected && selected.entry === next.entry && selected.added === next.added) return;
      const toSet = Boolean(selected) && selected.entry === next.entry && next.added;
      selected = next;
      filter(next);
      // A format switch starts the rail or strip from its start (owner
      // ruling, 21 Oct 2026), then the current thumbnail is brought into view.
      strip.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      const visible = shown();
      show((toSet && visible.find((slide) => slide.dataset.dgUpgradeOf)) || visible[0]);
      edges();
    }

    strip.addEventListener('click', (event) => {
      const thumb = event.target instanceof Element && event.target.closest('[data-dg-thumb]');
      if (thumb) show(slides[thumbs.indexOf(thumb)]);
    });

    // The current thumbnail follows the main image, however it was moved.
    let frame = 0;
    track.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        const width = track.clientWidth;
        if (width) mark(shown()[Math.round(track.scrollLeft / width)]);
      });
    }, { passive: true });

    if (formats) {
      root.addEventListener('dg:pdp', follow);
      follow();
      continue;
    }

    // After the buy box's own change handler, which keeps the variant id in step.
    root.addEventListener('change', (event) => {
      if (!event.target.matches?.('[data-dg-option], [data-dg-choice]')) return;
      if (owned) choose();
      else show(slideFor(chosenId()));
    });

    if (owned) {
      choose(true);
      continue;
    }

    const start = slideFor(chosenId());
    if (start && start !== slides[0]) show(start);
  }
})();

/*
 * LOOK INSIDE (pdp-redesign piece 5). The button opens its native modal
 * dialog; the close button, Escape (native) and a click or tap on the
 * backdrop close it, and closing returns focus to the button that opened it.
 * The backdrop (owner ruling, 23 Oct 2026) counts only a press that both
 * starts and ends outside the dialog's box, so a drag or a magnifier gesture
 * released out there does not close it; a click inside the dialog but
 * outside the contents sheet closes only the sheet, as before. Below 768 the
 * dialog fills the viewport, so there is no backdrop to press. The dialog's
 * size is CSS's, not its content's, so nothing moves as its images load.
 *
 * THE PAGE VIEWER (owner ruling, 22 Oct 2026): one page per slide on a
 * natively snapping track. Every opening starts at page 1, except from an
 * opener carrying data-dg-inside-start="workbook" (an FAQ answer's
 * #look-inside-workbook link, all-PDP copy, 23 Oct 2026), which starts at the
 * first workbook page (data-dg-inside-workbook, dg-pdp-aside.liquid), or at
 * page 1 where there is none. The previous and
 * next buttons and the Left and Right arrow keys move exactly one page, by
 * the browser's own smooth scroll (instant under prefers-reduced-motion).
 * The page shown is read from the scroll position (an IntersectionObserver
 * on the track: the slide more than half in view), so swipe, buttons and
 * keys always agree; the bottom bar and the arrows' aria-disabled at the two
 * ends follow it. A press during a moving scroll steps on from where that
 * scroll is going, not from where it has got to.
 * Page 1 loads with the page at low priority; page 2 on the first sign of
 * intent (below).
 * Opening moves focus to the track (owner feedback, 22 Oct 2026), not the
 * close button the dialog would focus first; the track draws no ring.
 *
 * THE BOTTOM BAR AND THE CONTENTS SHEET (owner rulings, 22 Oct 2026). A run
 * is a contiguous stretch of slides with the same data-book and data-row (the
 * snippet parses both from the alt text). The centre control shows the run's
 * section and one dot per page in it (none above 12), the current page's
 * filled; as a button its name is "Contents: <book>, <section>, page n of m",
 * and the live region speaks the same position. The book pills are a tablist:
 * the current book's pill is selected, and a pill, or Left and Right among
 * the pills, jumps to its book's first page. The centre button opens the
 * sheet, built here from the runs: per book a heading, then one row per run,
 * the current one marked (aria-current). A row jumps to its run's first page,
 * closes the sheet and returns focus to the track. Escape, the scrim or the
 * centre button close the sheet, not the dialog, and return focus to the
 * centre button. The sheet lies over the page area (dg-pdp.css), so the
 * dialog keeps its size; a wheel or a drag on it never scrolls the page
 * behind. An arrow step or a pill closes it first, and Left and Right do not
 * step while it is open.
 *
 * THE MAGNIFIER (owner ruling, 22 Oct 2026): a loupe, not tap-to-zoom, so
 * there is no zoomed state to get stuck in. On touch, a press held 200ms
 * without moving more than 8px shows it; moving sooner is a swipe and turns
 * the page as before. While held, the loupe follows the finger and the track
 * does not scroll (touchmove is cancelled); lifting or touchcancel removes
 * it, and the long-press menu is stopped. A mouse shows it while the pointer
 * is over the page image, with no click. Keyboard users get nothing new; the
 * alt text stands. The loupe is a band the dialog's full inner width (owner
 * rulings after the iPhone checks, 22 Oct 2026). Its zoom fits the page's
 * text column (TEXT_COLUMN of the page's width) to the band less its inset,
 * from 1.1x to 2.5x, so a whole line shows: the column is centred in the band and does
 * not follow the finger sideways, and the view follows it up and down,
 * clamped so it never shows past the page's top or bottom. It is drawn from
 * the 2000-wide file (the slide's own image lies under it until that has
 * arrived). PREFETCH (owner ruling, 23 Oct 2026): once a page has settled in
 * the open dialog (SETTLE, 500ms, without a scroll) and its own image has loaded,
 * its 2000-wide file is fetched at low priority by a detached image, so a
 * page that has sat for a moment magnifies sharp from the first frame. A
 * touch on the page, or a mouse moving onto it, starts the fetch if it has
 * not started. Each file is fetched once per page view, never before the
 * dialog first opens; pages passed without settling are not. On touch its
 * centre sits the lift (--inside-loupe-lift, dg-pdp.css) above the finger; on
 * a mouse it is centred vertically on the pointer. It never flips (round 6,
 * owner ruling, 22 Oct 2026): it follows the point until its edge meets the
 * dialog's inner top or bottom and stays pinned there, while the view inside
 * keeps tracking, so the page's first line shows as the finger reaches the
 * page's top, and its last line at the bottom. It is aria-hidden, and nothing
 * about it is announced.
 * THE HINT (round 6, owner ruling). On touch it shows whenever the viewer is
 * at rest: centred in the empty space above the current page, or over the
 * page's bottom where that space cannot hold it (the close button counts as
 * taken); it goes while the band shows or the sheet is open, and comes back
 * when the finger lifts. With a hover-capable fine pointer it is round 3's:
 * over the first page on the first opening only, gone on the first loupe or
 * page turn, once per page view.
 * pdp_look_inside_magnified fires the first time the loupe shows in each
 * opening, through DGEvents.track, as pdp_look_inside_opened does.
 */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  // A dialog can have more than one opener (the aside's button and the What's
  // inside row's link); each dialog is set up once, and closing returns focus
  // to whichever opened it.
  const setups = new Map();
  for (const open of document.querySelectorAll('[data-dg-inside-open]')) {
    const dialog = open.parentElement.querySelector('[data-dg-inside]') || document.querySelector('[data-dg-inside]');
    if (!dialog) continue;
    if (!setups.has(dialog)) setups.set(dialog, setUp(dialog));
    const { warm, show } = setups.get(dialog);
    for (const type of ['pointerenter', 'focus', 'touchstart']) open.addEventListener(type, warm, { once: true, passive: true });
    open.addEventListener('click', () => show(open));
    if (open.querySelector('.dg-eyes')) lookOnCue(open);
  }

  // THE EYES (owner ruling, 23 Oct 2026): the sequence plays once when the
  // button is first 60% on screen, then on a mouse entering it and on keyboard
  // focus, never over itself. dg-pdp.css draws it; reduced motion skips it.
  function lookOnCue(btn) {
    const play = () => {
      if (reduced.matches) return;
      btn.classList.remove('is-looking');
      void btn.offsetWidth;
      btn.classList.add('is-looking');
    };
    btn.addEventListener('animationend', (event) => {
      if (event.target.matches?.('.dg-eyes__eye')) btn.classList.remove('is-looking');
    });
    if ('IntersectionObserver' in window) {
      const seen = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.intersectionRatio >= 0.6)) return;
        seen.unobserve(btn);
        play();
      }, { threshold: 0.6 });
      seen.observe(btn);
    }
    btn.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse' && !btn.classList.contains('is-looking')) play();
    });
    btn.addEventListener('focus', () => {
      if (btn.matches(':focus-visible') && !btn.classList.contains('is-looking')) play();
    });
  }

  function setUp(dialog) {
    const track = dialog.querySelector('[data-dg-inside-track]');
    const slides = track ? [...track.children] : [];
    const count = dialog.querySelector('[data-dg-inside-count]');
    const [previous, next] = dialog.querySelectorAll('[data-dg-inside-step]');
    const where = dialog.querySelector('[data-dg-inside-where]');
    const sectionName = where?.querySelector('[data-dg-inside-section]');
    const dots = where?.querySelector('[data-dg-inside-dots]');
    const tabs = [...dialog.querySelectorAll('[data-dg-inside-books] [role="tab"]')];
    const sheet = dialog.querySelector('[data-dg-inside-sheet]');
    const box = sheet?.querySelector('[data-dg-inside-runs]');
    const toggle = where instanceof HTMLButtonElement ? where : null;
    const loupe = dialog.querySelector('[data-dg-inside-loupe]');
    const hint = dialog.querySelector('[data-dg-inside-hint]');
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    let current = 0;
    let target = null;
    let shownRun = null;

    // Runs of slides with the same book and sheet row, in order.
    const runs = [];
    slides.forEach((slide, index) => {
      const { book = '', family = '', section = '', row = '' } = slide.dataset;
      const last = runs[runs.length - 1];
      if (last && last.book === book && last.row === row) last.end = index;
      else runs.push({ book, family, section, row, start: index, end: index, item: null });
    });
    const runAt = (index) => runs.find((run) => index >= run.start && index <= run.end);
    const fill = (template, run) => template.replace('[book]', run.book).replace('[section]', run.section || run.book).replace('[current]', current - run.start + 1).replace('[total]', run.end - run.start + 1);

    const sync = () => {
      const run = runAt(current);
      if (run) {
        if (run !== shownRun && sectionName) {
          sectionName.textContent = run.section || run.book;
          dots?.replaceChildren(...(run.end - run.start < 12 ? slides.slice(run.start, run.end + 1).map(() => {
            const dot = document.createElement('span');
            dot.className = 'dg-inside__dot';
            return dot;
          }) : []));
          shownRun = run;
        }
        if (dots) [...dots.children].forEach((dot, index) => dot.toggleAttribute('data-current', index === current - run.start));
        if (toggle) toggle.setAttribute('aria-label', fill(toggle.dataset.name, run));
        if (count) count.textContent = fill(count.dataset.template, run);
        for (const tab of tabs) {
          const selected = tab.dataset.book === run.book;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
        }
        for (const other of runs) other.item?.setAttribute('aria-current', String(other === run));
      }
      if (current !== 0) hintSpent = true;
      refreshHint();
      previous?.setAttribute('aria-disabled', String(current <= 0));
      next?.setAttribute('aria-disabled', String(current >= slides.length - 1));
    };

    const go = (index, instant = false) => {
      const to = Math.max(0, Math.min(slides.length - 1, index));
      target = instant ? null : to;
      track.scrollTo({ left: to * track.clientWidth, behavior: instant || reduced.matches ? 'instant' : 'smooth' });
      if (instant) { current = to; sync(); }
    };

    const step = (by) => go((target ?? current) + by);

    // THE SHEET.
    let swallow = false;
    const sheeted = () => Boolean(sheet && !sheet.hidden);
    const showSheet = (open, focusTo = null) => {
      if (!sheet || !toggle || open === sheeted()) return;
      sheet.hidden = !open;
      toggle.setAttribute('aria-expanded', String(open));
      if (open) hideLoupe();
      refreshHint();
      if (open) {
        const item = runAt(current)?.item;
        if (item) {
          box.scrollTop = item.offsetTop - (box.clientHeight - item.offsetHeight) / 2;
          item.focus({ preventScroll: true });
        }
      } else {
        focusTo?.focus({ preventScroll: true });
      }
    };

    // THE HINT. Placed in pixels, relative to the dialog's padding box.
    let hintSpent = false;
    const placeHint = () => {
      const figure = slides[current]?.querySelector('.dg-inside__figure');
      if (!figure) return;
      const frame = dialog.getBoundingClientRect();
      const style = getComputedStyle(dialog);
      const hintStyle = getComputedStyle(hint);
      const room = parseFloat(hintStyle.getPropertyValue('--inside-hint-room')) || 0;
      const inset = parseFloat(hintStyle.getPropertyValue('--inside-hint-inset')) || 0;
      const at = (rect) => ({ left: rect.left - frame.left - dialog.clientLeft, top: rect.top - frame.top - dialog.clientTop, width: rect.width, height: rect.height });
      const page = at(figure.getBoundingClientRect());
      const width = hint.offsetWidth;
      const height = hint.offsetHeight;
      const padTop = parseFloat(style.paddingTop) || 0;
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const inner = dialog.clientWidth - padLeft - (parseFloat(style.paddingRight) || 0);
      let left = padLeft + (inner - width) / 2;
      let top = padTop + (page.top - padTop - height) / 2;
      let place = 'above';
      const close = dialog.querySelector('[data-dg-inside-close]');
      const shut = close ? at(close.getBoundingClientRect()) : null;
      const clear = (box) => !box || left + width + room <= box.left || left >= box.left + box.width + room || top + height + room <= box.top || top >= box.top + box.height + room;
      if (fine.matches || page.top - padTop < height + 2 * room || !clear(shut)) {
        left = page.left + (page.width - width) / 2;
        top = page.top + page.height - inset - height;
        place = 'over';
      }
      hint.dataset.place = place;
      hint.style.translate = `${left}px ${top}px`;
    };
    const refreshHint = () => {
      if (!hint) return;
      const busy = !dialog.open || sheeted() || (loupe && !loupe.hidden);
      const wanted = fine.matches ? !hintSpent && current === 0 : true;
      hint.hidden = busy || !wanted;
      if (!hint.hidden) placeHint();
    };

    const jumpToBook = (tab) => {
      const first = slides.findIndex((slide) => slide.dataset.book === tab.dataset.book);
      if (first < 0) return;
      showSheet(false);
      go(first, true);
    };

    if (track && slides.length) {
      const seen = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          current = slides.indexOf(entry.target);
          if (current === target) target = null;
          sync();
        }
      }, { root: track, threshold: 0.5 });
      slides.forEach((slide) => seen.observe(slide));
      // A finger on the track takes over from any step still under way.
      track.addEventListener('pointerdown', () => { target = null; }, { passive: true });
      for (const button of [previous, next]) {
        button?.addEventListener('click', () => {
          if (button.getAttribute('aria-disabled') === 'true') return;
          showSheet(false);
          step(Number(button.dataset.dgInsideStep));
        });
      }
      for (const tab of tabs) tab.addEventListener('click', () => jumpToBook(tab));
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && sheeted()) {
          event.preventDefault();
          swallow = true;
          setTimeout(() => { swallow = false; });
          showSheet(false, toggle);
          return;
        }
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        if (sheeted()) return;
        event.preventDefault();
        const by = event.key === 'ArrowLeft' ? -1 : 1;
        const at = tabs.indexOf(event.target);
        if (at < 0) {
          step(by);
          return;
        }
        // Among the pills: the tablist's own arrow keys, which also jump.
        const tab = tabs[(at + by + tabs.length) % tabs.length];
        jumpToBook(tab);
        tab.focus({ preventScroll: true });
      });
    }

    if (sheet && toggle && box && track) {
      let group = null;
      runs.forEach((run, index) => {
        if (!group || group.book !== run.book) {
          const heading = document.createElement('p');
          heading.className = 'dg-inside__book';
          heading.id = `${sheet.id}-${index}`;
          if (run.family) heading.dataset.family = run.family;
          const dot = document.createElement('span');
          dot.className = 'dg-inside__book-dot';
          dot.setAttribute('aria-hidden', 'true');
          heading.append(dot, run.book);
          const rows = document.createElement('ul');
          rows.className = 'dg-inside__rows';
          rows.setAttribute('aria-labelledby', heading.id);
          box.append(heading, rows);
          group = { book: run.book, rows };
        }
        const li = document.createElement('li');
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'dg-inside__row';
        item.textContent = run.row || run.book;
        item.addEventListener('click', () => {
          go(run.start, true);
          showSheet(false, track);
        });
        li.append(item);
        group.rows.append(li);
        run.item = item;
      });

      toggle.addEventListener('click', () => showSheet(!sheeted(), toggle));
      sheet.querySelector('[data-dg-inside-scrim]')?.addEventListener('click', () => showSheet(false, toggle));
      // Escape while the sheet is open closes the sheet only (the keydown
      // above); this holds the dialog open where an engine raises cancel
      // regardless.
      dialog.addEventListener('cancel', (event) => {
        if (!sheeted() && !swallow) return;
        event.preventDefault();
        showSheet(false, toggle);
      });
      dialog.addEventListener('close', () => showSheet(false));

      // A wheel or a drag that the rows cannot take any further, or that
      // starts outside them (the scrim, the handle), stops here, so the page
      // behind never scrolls.
      const stuck = (event, down) => !box.contains(event.target) || (down ? box.scrollTop + box.clientHeight >= box.scrollHeight - 1 : box.scrollTop <= 0);
      sheet.addEventListener('wheel', (event) => {
        if (stuck(event, event.deltaY > 0)) event.preventDefault();
      }, { passive: false });
      let touchY = 0;
      sheet.addEventListener('touchstart', (event) => { touchY = event.touches[0].clientY; }, { passive: true });
      sheet.addEventListener('touchmove', (event) => {
        const y = event.touches[0].clientY;
        if (event.cancelable && stuck(event, y < touchY)) event.preventDefault();
        touchY = y;
      }, { passive: false });
    }

    // THE MAGNIFIER.
    // The page's text column, as a share of the page's width (owner ruling,
    // 22 Oct 2026). Measured from the 55 sample pages' 2000-wide files on 23
    // Oct 2026: the widest text column is 0.884 (the Grandparents chapter
    // pages, 5.8% white each side; body pages run 0.872 to 0.884, notebook
    // prompts to 0.882, their ruled lines aside). The ruling's 0.878 would
    // clip those by about 0.3% of the page. New files with a wider column
    // need this re-measured.
    const TEXT_COLUMN = 0.884;
    // PREFETCH: the detached images are kept so a fetch is not dropped with them.
    const SETTLE = 500; // owner ruling, 23 Oct 2026
    const fetched = new Map();
    const prefetch = (figure) => {
      const large = figure?.dataset.dgInsideLoupeSrc;
      if (!large || !dialog.open || fetched.has(large)) return;
      const image = new Image();
      image.fetchPriority = 'low';
      image.decoding = 'async';
      image.src = large;
      fetched.set(large, image);
    };
    let settling = 0;
    const settle = () => {
      clearTimeout(settling);
      settling = setTimeout(() => {
        const figure = slides[current]?.querySelector('.dg-inside__figure');
        const img = figure?.querySelector('img');
        if (!img || !dialog.open) return;
        if (img.complete && img.naturalWidth) prefetch(figure);
        else img.addEventListener('load', () => { if (slides[current]?.contains(figure)) prefetch(figure); }, { once: true });
      }, SETTLE);
    };
    let magnified = false;
    const hideLoupe = () => {
      if (!loupe || loupe.hidden) return;
      loupe.hidden = true;
      refreshHint();
    };
    const showLoupe = (figure, x, y, lifted) => {
      const img = figure.querySelector('img');
      if (!loupe || !img) return;
      if (loupe.hidden) {
        loupe.hidden = false;
        hintSpent = true;
        refreshHint();
        if (!magnified) {
          magnified = true;
          window.DGEvents?.track('pdp_look_inside_magnified', {}, { origin: opener });
        }
      }
      const large = figure.dataset.dgInsideLoupeSrc;
      if (loupe.dataset.src !== large) {
        loupe.dataset.src = large;
        if (!fetched.has(large)) fetched.set(large, null);
        loupe.style.backgroundImage = `url("${large}"), url("${img.currentSrc || img.src}")`;
      }
      // The band is the dialog's full inner width, inside its safe-area
      // padding (owner ruling, 22 Oct 2026); its height is CSS's.
      const page = figure.getBoundingClientRect();
      const frame = dialog.getBoundingClientRect();
      const style = getComputedStyle(dialog);
      const padLeft = parseFloat(style.paddingLeft) || 0;
      const padRight = parseFloat(style.paddingRight) || 0;
      loupe.style.width = `${dialog.clientWidth - padLeft - padRight}px`;
      const height = loupe.offsetHeight;
      const innerX = loupe.clientWidth;
      const innerY = loupe.clientHeight;
      // The zoom fits the page's text column to the band, less its inset
      // (dg-pdp.css) each side, so a whole line shows and the reader only
      // moves up and down; 1.1x to 2.5x.
      const loupeStyle = getComputedStyle(loupe);
      const inset = parseFloat(loupeStyle.getPropertyValue('--inside-loupe-inset')) || 0;
      const zoom = Math.max(1.1, Math.min(2.5, (innerX - 2 * inset) / (page.width * TEXT_COLUMN)));
      loupe.dataset.zoom = zoom.toFixed(3);
      // Across: the page's centre (the column's) at the band's centre, whatever
      // the finger does. Down: centred on the point, clamped to the page.
      const halfY = innerY / 2 / zoom;
      const py = Math.max(halfY, Math.min(page.height - halfY, y - page.top));
      loupe.style.backgroundSize = `${page.width * zoom}px ${page.height * zoom}px`;
      loupe.style.backgroundPosition = `${innerX / 2 - (page.width / 2) * zoom}px ${innerY / 2 - py * zoom}px`;
      const lift = lifted ? parseFloat(loupeStyle.getPropertyValue('--inside-loupe-lift')) || height : 0;
      // No flip: the band stops at the dialog's inner top and bottom (inside
      // the safe-area padding) and stays there while the view keeps tracking.
      const atY = y - frame.top - dialog.clientTop;
      const padTop = parseFloat(style.paddingTop) || 0;
      const padBottom = parseFloat(style.paddingBottom) || 0;
      const top = Math.max(padTop, Math.min(dialog.clientHeight - padBottom - height, atY - lift - height / 2));
      const left = padLeft;
      loupe.style.translate = `${left}px ${top}px`;
    };

    if (track && loupe) {
      const figureAt = (target) => (target instanceof Element ? target.closest('.dg-inside__figure') : null);
      let hold = null;
      let touchedAt = 0;
      const endHold = () => {
        if (hold) clearTimeout(hold.timer);
        hold = null;
        hideLoupe();
      };
      track.addEventListener('touchstart', (event) => {
        endHold();
        touchedAt = Date.now();
        prefetch(figureAt(event.target));
        const figure = event.touches.length === 1 ? figureAt(event.target) : null;
        if (!figure) return;
        const { clientX: x, clientY: y } = event.touches[0];
        const held = { figure, startX: x, startY: y, x, y, on: false };
        held.timer = setTimeout(() => {
          held.on = true;
          showLoupe(figure, held.x, held.y, true);
        }, 200);
        hold = held;
      }, { passive: true });
      track.addEventListener('touchmove', (event) => {
        if (!hold) return;
        const { clientX: x, clientY: y } = event.touches[0];
        if (hold.on) {
          if (event.cancelable) event.preventDefault();
          showLoupe(hold.figure, x, y, true);
        } else if (Math.hypot(x - hold.startX, y - hold.startY) > 8) {
          endHold();
        } else {
          hold.x = x;
          hold.y = y;
        }
      }, { passive: false });
      track.addEventListener('touchend', endHold, { passive: true });
      track.addEventListener('touchcancel', endHold, { passive: true });
      track.addEventListener('contextmenu', (event) => {
        if (figureAt(event.target) && (event.pointerType === 'touch' || Date.now() - touchedAt < 1500)) event.preventDefault();
      });
      // WebKit raises a mousemove as the pages scroll under a still pointer
      // (its movementX stays 0 even for real moves, so that cannot tell them
      // apart). Only a move to a new place shows the loupe, so a step does not
      // light up, and fetch the large file of, every page it passes. The last
      // place is kept wherever the mouse was, the opener's click included.
      let mouseAt = '';
      const placed = (event) => `${event.screenX},${event.screenY}`;
      for (const type of ['pointermove', 'pointerdown']) {
        document.addEventListener(type, (event) => {
          if (event.pointerType === 'mouse') mouseAt = placed(event);
        }, { passive: true });
      }
      track.addEventListener('pointermove', (event) => {
        if (event.pointerType !== 'mouse' || placed(event) === mouseAt) return;
        const figure = figureAt(event.target);
        if (figure && !sheeted()) showLoupe(figure, event.clientX, event.clientY, false);
        else hideLoupe();
      });
      track.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') hideLoupe();
      });
      // A step under a resting mouse moves the page away from the loupe.
      track.addEventListener('scroll', () => {
        if (!hold) hideLoupe();
        settle();
      }, { passive: true });
      dialog.addEventListener('close', () => {
        endHold();
        clearTimeout(settling);
      });
    }

    // INTENT (owner ruling, 22 Oct 2026): page 1 loads with the page at low
    // priority (the snippet). The first pointer on, focus on or touch of the
    // button turns page 2 eager and decodes pages 1 and 2, once per page view,
    // so page 1 paints on open and page 2 is ready for the first step; pages 3
    // on stay lazy.
    let warmed = false;
    const warm = () => {
      if (warmed) return;
      warmed = true;
      for (const slide of slides.slice(0, 2)) {
        const img = slide.querySelector('img');
        if (!img) continue;
        img.loading = 'eager';
        img.decode?.().catch(() => {});
      }
    };

    let opener = null;
    const show = (open) => {
      opener = open;
      magnified = false;
      warm();
      showSheet(false);
      hideLoupe();
      dialog.showModal();
      if (track && slides.length) {
        const start = open.dataset.dgInsideStart === 'workbook' ? slides.findIndex((slide) => slide.hasAttribute('data-dg-inside-workbook')) : 0;
        go(Math.max(0, start), true);
        track.focus({ preventScroll: true });
        if (loupe) settle();
      }
      refreshHint();
      window.DGEvents?.track('pdp_look_inside_opened', {}, { origin: open });
    };
    dialog.querySelector('[data-dg-inside-close]')?.addEventListener('click', () => dialog.close());
    // THE BACKDROP. A press on it reaches the dialog itself, at a point
    // outside its box; one that started inside (a drag, a held loupe) is not
    // a backdrop press, wherever it ends.
    const outside = (event) => {
      if (event.target !== dialog) return false;
      const box = dialog.getBoundingClientRect();
      return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
    };
    let pressedOutside = false;
    dialog.addEventListener('pointerdown', (event) => { pressedOutside = outside(event); });
    dialog.addEventListener('click', (event) => {
      const backdrop = pressedOutside && outside(event);
      pressedOutside = false;
      if (backdrop) dialog.close();
    });
    dialog.addEventListener('close', () => {
      // A mouse's one showing is spent once the first opening closes.
      if (hint && !hint.hidden) hintSpent = true;
      refreshHint();
      opener?.focus();
    });
    // The page box moves with the viewport; the hint follows it.
    if (hint) new ResizeObserver(() => { if (!hint.hidden) placeHint(); }).observe(dialog);
    return { warm, show };
  }
})();

/*
 * AN FAQ ANSWER'S #select-paperback LINK (all-PDP copy, owner, 23 Oct 2026;
 * dg-faq-answer.liquid). The button names the Paperback option value; this
 * checks that format tile's radio and dispatches change, so the buy box's own
 * handler applies it as if it were clicked, then brings the format row into
 * view and moves focus to the radio. The answers are in the FAQ section
 * (sections/dg-product-faq.liquid, owner ruling, 23 Oct 2026), outside the
 * product section, so a button with no product section around it reaches the
 * page's one product section.
 */
(() => {
  for (const button of document.querySelectorAll('[data-dg-select-format]')) {
    const root = button.closest('[data-dg-product]') || document.querySelector('[data-dg-product]');
    const radio = [...(root?.querySelectorAll('[data-dg-option="0"]') || [])].find((input) => input.value === button.dataset.dgSelectFormat);
    if (!radio) continue;
    button.addEventListener('click', () => {
      if (!radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      }
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      (radio.closest('.dg-format') || radio).scrollIntoView({ block: 'center', behavior: reduced ? 'instant' : 'smooth' });
      radio.focus({ preventScroll: true });
    });
  }
})();

/*
 * THE STICKY COLUMN FOLLOWS THE HEADER (tweak round 1, owner, 7 Oct 2026).
 * The header's bar hides on scroll down and returns on scroll up
 * (dg-header.js sets data-dg-header-hidden; dg-kit.css, "The bar", decides
 * whether that hides it). This measures the bar's height into
 * --dg-shell-bar-height and marks the product data-dg-header-away exactly
 * while the bar's hidden rule matches, so the sticky left column sits under
 * the bar when it shows and at the top of the screen when it does not
 * (dg-pdp.css). The selectors are the bar's own, from dg-kit.css: hover holds
 * the bar only where hover opens the guides panel.
 */
(() => {
  const shell = document.querySelector('.dg-shell[data-dg-hide-on-scroll]');
  const bar = shell?.querySelector('.dg-shell__bar');
  const products = document.querySelectorAll('[data-dg-product]');
  if (!bar || !products.length) return;

  const HIDDEN = '.dg-shell[data-dg-header-hidden]:not([data-dg-menu-open]) .dg-shell__bar';
  const hover = window.matchMedia('(hover: hover)');
  const away = () => bar.matches(hover.matches
    ? `${HIDDEN}:not(:focus-within, :has(.dg-guides[data-dg-open], .dg-guides:hover))`
    : `${HIDDEN}:not(:focus-within, :has(.dg-guides[data-dg-open]))`);

  function mark() {
    const hidden = away();
    for (const product of products) product.toggleAttribute('data-dg-header-away', hidden);
  }

  const measure = () => document.documentElement.style.setProperty('--dg-shell-bar-height', `${bar.offsetHeight}px`);
  measure();
  new ResizeObserver(measure).observe(bar);
  for (const product of products) product.setAttribute('data-dg-header-follow', '');

  new MutationObserver(mark).observe(shell, {
    attributes: true,
    attributeFilter: ['data-dg-header-hidden', 'data-dg-menu-open', 'data-dg-open'],
    subtree: true,
  });
  bar.addEventListener('focusin', mark);
  bar.addEventListener('focusout', () => requestAnimationFrame(mark));
  for (const guides of bar.querySelectorAll('.dg-guides')) {
    guides.addEventListener('mouseenter', mark);
    guides.addEventListener('mouseleave', mark);
  }
  hover.addEventListener('change', mark);
  mark();
})();

/*
 * WHAT RENDERS UNDER THE MAIN IMAGE (owner ruling, 15 Oct 2026). From 990 the
 * sticky left column holds the gallery, then Look inside and the author card
 * where each renders, and the whole column fits the screen while sticky with
 * the header hidden: the main image takes what is left (dg-pdp.css, "THE
 * GALLERY RAIL FROM 990"). This measures what the column shows under the
 * gallery, with the column's gap before each, into --rail-under on the
 * column, so a product without Look inside or without the card gives that
 * space back to the image. Only what is displayed counts: the card's copy
 * above the accordions is display: none from 990 and is not in the column.
 * Below 990 the column dissolves and nothing reads the property, so it is
 * cleared. The card's width follows the image, so its
 * height can change as the image resizes; the observer settles it.
 */
(() => {
  const wide = window.matchMedia('(min-width: 990px)');
  for (const column of document.querySelectorAll('.dg-product__left')) {
    const media = column.querySelector(':scope > .dg-product__media');
    if (!media) continue;
    const under = [...column.children].filter((child) => child !== media);

    function measure() {
      if (!wide.matches) {
        column.style.removeProperty('--rail-under');
        return;
      }
      const gap = parseFloat(getComputedStyle(column).rowGap) || 0;
      let total = 0;
      for (const child of under) {
        if (!child.getClientRects().length) continue;
        total += gap + child.getBoundingClientRect().height;
      }
      column.style.setProperty('--rail-under', `${total}px`);
    }

    const observer = new ResizeObserver(measure);
    for (const child of under) observer.observe(child);
    wide.addEventListener('change', measure);
    measure();
  }
})();

/*
 * THE FAQ CARDS OPEN ON HOVER, WITH INTENT, AND OPEN AND CLOSE EASED (owner
 * rulings, 23 Oct 2026; sections/dg-product-faq.liquid), only where the
 * device hovers with a fine pointer, and only on the pointer's own
 * movement: a pointermove counts only when the pointer's position has
 * changed since the last one (or since the last wheel event, which carries
 * it), so the page scrolling under a still pointer opens and closes
 * nothing. A card opens once the pointer has been inside it for
 * --dg-delay-disclosure-intent-open (200ms; the nav panel's 120ms reads as
 * instant on a card), moving or resting, and leaving first cancels it; a
 * card the hover opened closes --dg-delay-hover-intent-close after the
 * pointer leaves, and coming back first cancels it. A click
 * anywhere on a card the hover opened pins it open: on its question the
 * native toggle, which would close it, is cancelled. A pinned card stays
 * open when the pointer leaves, and a click on its question closes it. A
 * card opened by a click, a tap or the keyboard counts as pinned. Focus
 * opens nothing.
 * ONE OPEN AT A TIME (owner ruling, 23 Oct 2026, run 4), at every width and
 * for every input. A click, tap or key that opens or pins a card closes every
 * other card that is open, pinned or hover-opened, both moving at once. While
 * a card is pinned the hover opens nothing; with none pinned, a hover opening
 * closes any other card the hover opened that is still waiting out its close
 * delay. With no script a card is a plain <details>, and any number open.
 * Every opening and closing, by hover, click, tap or keyboard, animates the
 * card's height in script (the Web Animations API, so Safari and Firefox
 * ease as Chrome does) over --dg-duration-reveal on --dg-ease-reveal, or
 * --dg-duration-reduced under prefers-reduced-motion; the figures are read
 * from the tokens' custom properties when used. The open attribute is set
 * before an opening and removed after a closing, so it stays the source of
 * truth for state and aria-expanded, and find-in-page still opens a card
 * natively. While a card moves it carries data-dg-faq-motion, which clips
 * it and, while closing, turns its "−" back to "+" at the start
 * (dg-pdp.css). A request mid-motion starts from the card's height at that
 * moment.
 * A CARD OPENED OR PINNED BY A CLICK, TAP OR KEY IS BROUGHT INTO VIEW (owner
 * ruling, 23 Oct 2026, run 4); a hover opening never scrolls. The target is
 * taken from the layout the motion ends on: the card at its full height, and
 * every card above it at the height it is moving to. If the card's bottom
 * would end below the fold, the page scrolls until it sits --dg-space-5 above
 * it. The fold is the top of the purchase bar while it shows, otherwise the
 * bottom of the screen, above CookieYes's notice while that shows (dg-notice.js),
 * the edge the bar itself keeps clear of. A card too tall for the room under
 * the header instead has its top brought to --dg-space-4 under the header's
 * bar, or under the screen's top edge where the scroll will hide the bar,
 * the offset the sticky column keeps (dg-pdp.css). The page never scrolls up
 * to do this, and a card that fits is left where it is. One case does move
 * the page up: when the cards closing above it would carry the opened card's
 * question off the top, the page holds that question where it was tapped,
 * as the browser's own scroll anchoring would. Scroll anchoring is off while
 * the motion runs, so every engine lays the page out as the target assumes.
 * The scroll is written each frame from the opened card's own animation, so
 * it moves in step with the height on the same curve, and it stops for good
 * the moment the reader scrolls, wheels, touches or presses a key. Under
 * reduced motion it is one jump to the same target.
 * While the hover holds a card open it carries data-dg-hover="open",
 * "pinned" once clicked, and the attribute goes when it closes. The
 * accordion event below skips a card carrying it, so a hover opening sends
 * nothing and the pin sends pdp_accordion_opened here, once. A click that
 * reopens a card still closing sends it here too: its open attribute never
 * went, so no toggle follows. Closing a card sends nothing.
 */
(() => {
  const list = document.querySelector('[data-dg-faq-hover]');
  if (!list) return;
  // The cards of every group, in page order (dg-product-faq.liquid).
  const cards = [...list.querySelectorAll('.dg-pdp-faq__card')];
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const still = window.matchMedia('(prefers-reduced-motion: reduce)');
  const timers = new Map();
  const motions = new Map();
  const ends = new Map();
  let hovered = null;
  let lastX;
  let lastY;

  const root = document.documentElement;
  const token = (name) => getComputedStyle(root).getPropertyValue(name).trim();
  // A time token in ms, whether written in ms or s.
  function ms(name) {
    const value = token(name);
    return (Number.parseFloat(value) || 0) * (value.endsWith('ms') ? 1 : 1000);
  }
  // A length token in px: rem against the root's own size.
  function px(name) {
    const value = token(name);
    const number = Number.parseFloat(value) || 0;
    return value.endsWith('rem') ? number * Number.parseFloat(getComputedStyle(root).fontSize) : number;
  }
  const duration = () => ms(still.matches ? '--dg-duration-reduced' : '--dg-duration-reveal');
  const closing = (card) => card.dataset.dgFaqMotion === 'closing';
  const shown = (card) => card.open && !closing(card);
  const pinned = () => cards.some((card) => shown(card) && card.dataset.dgHover !== 'open');
  const track = (card) => window.DGEvents?.track('pdp_accordion_opened', { name: card.dataset.dgAcc }, { origin: card });

  function settle(card, opening) {
    motions.delete(card);
    delete card.dataset.dgFaqMotion;
    if (opening) return;
    if (card.dataset.dgHover === 'open') delete card.dataset.dgHover;
    card.open = false;
  }

  // From the card's height now to its open or closed height.
  function move(card, opening) {
    const from = card.getBoundingClientRect().height;
    motions.get(card)?.cancel();
    if (opening) card.open = true;
    const style = getComputedStyle(card);
    const edges = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
      .reduce((sum, edge) => sum + Number.parseFloat(style[edge]), 0);
    const to = opening ? card.getBoundingClientRect().height : card.querySelector(':scope > summary').getBoundingClientRect().height + edges;
    ends.set(card, to);
    const time = duration();
    const easing = token('--dg-ease-reveal');
    if (!time || !easing || from === to) {
      settle(card, opening);
      return;
    }
    card.dataset.dgFaqMotion = opening ? 'opening' : 'closing';
    const motion = card.animate({ height: [`${from}px`, `${to}px`] }, { duration: time, easing });
    motions.set(card, motion);
    motion.onfinish = () => settle(card, opening);
  }

  // Every other card that is open, or opening, closes alongside this one.
  function others(card) {
    for (const other of cards) {
      if (other === card) continue;
      clearTimeout(timers.get(other));
      if (!shown(other)) continue;
      delete other.dataset.dgHover;
      move(other, false);
    }
  }

  /*
   * Bringing the card into view. scrolling holds the run in progress; a new
   * one, or the reader's own scroll, wheel, touch or key, ends it.
   */
  let scrolling = null;
  const stop = () => {
    if (scrolling) cancelAnimationFrame(scrolling.frame);
    scrolling = null;
  };
  for (const type of ['wheel', 'touchstart', 'keydown']) {
    window.addEventListener(type, stop, { capture: true, passive: true });
  }

  function bring(card, before, scrollY) {
    const was = before.get(card);
    let top = was.top;
    for (const other of cards) {
      if (other === card) break;
      const height = motions.has(other) ? ends.get(other) : other.getBoundingClientRect().height;
      top += height - before.get(other).height;
    }
    const height = motions.has(card) ? ends.get(card) : card.getBoundingClientRect().height;
    const bar = document.querySelector('[data-dg-buybar][data-dg-shown]');
    const notice = Number.parseFloat(token('--dg-cookie-notice-height')) || 0;
    const fold = window.innerHeight - notice - (bar?.offsetHeight ?? 0);
    const header = document.querySelector('.dg-shell__bar')?.offsetHeight ?? 0;
    const away = Boolean(document.querySelector('[data-dg-product][data-dg-header-away]'));
    const clear = px('--dg-space-5');
    const under = px('--dg-space-4');

    // The cards closing above would take its question off the top: hold it.
    const held = top < Math.min(was.top, (away ? 0 : header) + under) ? top - was.top : 0;
    top -= held;
    let by = 0;
    if (top + height + clear > fold) {
      // A scroll at least the bar's height leaves the header hidden.
      const hidden = away || top - under >= header;
      const limit = (hidden ? 0 : header) + under;
      by = height + clear > fold - limit ? top - limit : top + height + clear - fold;
    }
    by = Math.max(0, by) + held;
    if (!by) return;

    const go = (y) => window.scrollTo({ top: y, behavior: 'instant' });
    if (!duration()) {
      go(scrollY + by);
      return;
    }

    const lead = motions.get(card);
    const clock = lead ?? new Animation(new KeyframeEffect(null, [], { duration: duration(), easing: token('--dg-ease-reveal') }));
    if (!lead) clock.play();
    const start = clock.effect.getComputedTiming().progress ?? 0;
    const run = { frame: 0, written: scrollY };
    scrolling = run;
    const step = () => {
      if (scrolling !== run) return;
      // The reader moved the page, or the card was asked to close: theirs
      // from here.
      if (Math.abs(window.scrollY - run.written) > 1 || clock.playState === 'idle') {
        stop();
        return;
      }
      const progress = clock.playState === 'finished' ? 1 : clock.effect.getComputedTiming().progress ?? 0;
      run.written = scrollY + by * (start < 1 ? (progress - start) / (1 - start) : 1);
      go(run.written);
      run.written = window.scrollY;
      if (progress < 1) run.frame = requestAnimationFrame(step);
      else scrolling = null;
    };
    run.frame = requestAnimationFrame(step);
  }

  // The click's change: every card's box before it, then the motions, then
  // the scroll from the layout they end on.
  let anchoring = 0;
  function act(card, change) {
    stop();
    const scrollY = window.scrollY;
    const before = new Map(cards.map((each) => [each, each.getBoundingClientRect()]));
    const run = ++anchoring;
    root.style.overflowAnchor = 'none';
    others(card);
    change();
    bring(card, before, scrollY);
    // Back on once the motion has ended and been laid out.
    setTimeout(() => requestAnimationFrame(() => {
      if (run === anchoring) root.style.removeProperty('overflow-anchor');
    }), duration());
  }

  function after(card, name, then) {
    clearTimeout(timers.get(card));
    timers.set(card, setTimeout(then, ms(name)));
  }

  function enter(card) {
    clearTimeout(timers.get(card));
    if (shown(card) || pinned()) return;
    after(card, '--dg-delay-disclosure-intent-open', () => {
      if (hovered !== card || shown(card) || pinned()) return;
      for (const other of cards) {
        if (other !== card && other.dataset.dgHover === 'open' && shown(other)) {
          clearTimeout(timers.get(other));
          move(other, false);
        }
      }
      card.dataset.dgHover = 'open';
      move(card, true);
    });
  }

  function leave(card) {
    clearTimeout(timers.get(card));
    if (card.dataset.dgHover !== 'open' || closing(card)) return;
    after(card, '--dg-delay-hover-intent-close', () => {
      if (hovered !== card && card.dataset.dgHover === 'open' && !closing(card)) move(card, false);
    });
  }

  function hover(card) {
    if (card === hovered) return;
    const left = hovered;
    hovered = card;
    if (left) leave(left);
    if (card) enter(card);
  }

  document.addEventListener('wheel', (event) => {
    lastX = event.clientX;
    lastY = event.clientY;
  }, { passive: true });
  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    const moved = event.clientX !== lastX || event.clientY !== lastY;
    lastX = event.clientX;
    lastY = event.clientY;
    if (!moved || !fine.matches) return;
    hover(event.target instanceof Element ? event.target.closest('[data-dg-faq-hover] .dg-pdp-faq__card') : null);
  }, { passive: true });
  // Out of the window: no pointermove follows, so this is the leave.
  document.addEventListener('pointerout', (event) => {
    if (event.pointerType === 'mouse' && !event.relatedTarget) hover(null);
  });

  for (const card of cards) {
    card.addEventListener('click', (event) => {
      const onQuestion = event.target.closest('summary')?.parentElement === card;
      clearTimeout(timers.get(card));
      if (card.dataset.dgHover === 'open') {
        if (onQuestion) event.preventDefault();
        card.dataset.dgHover = 'pinned';
        act(card, () => {
          if (closing(card)) move(card, true);
        });
        track(card);
        return;
      }
      if (!onQuestion) return;
      event.preventDefault();
      delete card.dataset.dgHover;
      if (shown(card)) {
        stop();
        move(card, false);
        return;
      }
      const reopening = card.open;
      act(card, () => move(card, true));
      if (reopening) track(card);
    });
  }
})();

/*
 * TWO MORE BEHAVIOUR EVENTS (shop events, owner brief, 9 Oct 2026), through
 * DGEvents.track (assets/dg-events.js). pdp_accordion_opened sends the row's
 * fixed name from data-dg-acc (the FAQ section's cards, dg-product-faq.liquid:
 * "questions" on a question, the item's kind on the others, one event for
 * each card opened), never its heading or a question, and only on opening. A card the
 * hover opened (data-dg-hover, above) sends nothing here; its pin sends the
 * event above. pdp_gift_recipient_opened is sent when the gift card's
 * "send to a recipient" box is ticked (dg-gift-card-recipient.liquid); nothing
 * typed into the form is read.
 */
(() => {
  // toggle does not bubble, so it is heard in the capture phase.
  document.addEventListener('toggle', (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || !details.matches('[data-dg-acc]:not([data-dg-hover])')) return;
    window.DGEvents?.track('pdp_accordion_opened', { name: details.dataset.dgAcc }, { origin: details });
  }, true);

  document.addEventListener('change', (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || box.type !== 'checkbox' || !box.checked || !box.closest('recipient-form')) return;
    window.DGEvents?.track('pdp_gift_recipient_opened', {}, { origin: box });
  });
})();
